import { useEffect, useState } from "react";

import { PlusIconButton } from "@/Common/Components/IconControls/PlusIconButton/PlusIconButton";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { GrowthChartApiClient } from "@/Patient/Api/GrowthChartApiClient";
import { VitalSignPercentileUtility } from "@/Patient/Helpers/VitalSignPercentileUtility";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";

import styles from "./VitalSignsView.module.scss";

interface IVitalSignsViewProps {
    currentPatient: Patient;
    vitalSign: MedicalItemVitalSign;
    showArrow?: boolean;
    showPlus?: boolean;
    cssClassName?: string;
    cssClassNameDate?: string;
    hideVitalSignLabel?: boolean;
    onVitalSignClick?: () => void;
    onArrowClick?: () => void;
    onPlusClick?: () => void;
}

function getVitalSignPatientData(patient: Patient): VitalSignPatientData {
    return {
        dateOfBirth: patient?.IndividualResponse?.DateOfBirth,
        gender: patient?.Gender(),
        contactFacetId: patient?.InactivableResponse?.Id,
        contactId: patient?.IndividualResponse?.Contact_id,
        isPremature: patient?.IndividualResponse?.IsBornPremature,
        bornPrematureWeeks: patient?.IndividualResponse?.BornPrematureWeeks
    };
}

const emptyText = "--";

function VitalSignsView(props: IVitalSignsViewProps): JSX.Element {
    const { getText } = useTranslation();

    const [vitalSignPercentileUtility, setVitalSignPercentileUtility] = useState<VitalSignPercentileUtility>(null);
    const bloodPressure = (props.vitalSign && props.vitalSign.BPSys && props.vitalSign.BPDia) ? props.vitalSign.BPSys.toString() + "/" + props.vitalSign.BPDia.toString() : emptyText;
    const weight = (props.vitalSign && props.vitalSign.Weight) ? props.vitalSign.Weight.toString() + cacheStore.GetLocalizedValueByLookupId(props.vitalSign.WeightUnitLookupId) : emptyText;
    const height = (props.vitalSign && props.vitalSign.Height) ? props.vitalSign.Height.toString() + cacheStore.GetLocalizedValueByLookupId(props.vitalSign.HeightUnitLookupId) : emptyText;
    const bmi = (props.vitalSign && props.vitalSign.MassBodyIndex) ? props.vitalSign.MassBodyIndex.toFixed(2).toString() : emptyText;
    const waistCircumference = (props.vitalSign && props.vitalSign.WaistCircumference) ? props.vitalSign.WaistCircumference.toString() + cacheStore.GetLocalizedValueByLookupId(props.vitalSign.WaistCircumferenceUnitLookupId) : emptyText;
    const crcl = (props.vitalSign && props.vitalSign.CreatinineClearance) ? props.vitalSign.CreatinineClearance.toFixed(2).toString() : emptyText;
    const crclAlgorithm = props.vitalSign?.CreatinineClearanceAlgorythmLookupValue ? props.vitalSign.CreatinineClearanceAlgorythmLookupValue : emptyText;
    const o2 = (props.vitalSign && props.vitalSign.O2Sat) ? props.vitalSign.O2Sat.toString() + "%" : emptyText;
    const temperature = (props.vitalSign && props.vitalSign.Temperature) ? props.vitalSign.Temperature.toString() + cacheStore.GetLocalizedValueByLookupId(props.vitalSign.TemperatureUnitLookupId) : emptyText;
    const pharmaStartDateParsed = props.vitalSign?.PharmaStartDate ? LuxonDateTimeParser.parse(props.vitalSign.PharmaStartDate) : null;
    const vitalSignDate = (props.vitalSign && props.vitalSign.PharmaStartDate) ? pharmaStartDateParsed.toFormat("yyyy-MM-dd") : emptyText;
    const vitalSignHour = (props.vitalSign?.PharmaStartDate) ? pharmaStartDateParsed.toFormat("HH:mm:ss") : emptyText;
    const headCircumference = (props.vitalSign && props.vitalSign.HeadCircumference) ? props.vitalSign.HeadCircumference.toString() : emptyText;

    const headCircumferenceAgeLimit = 5;
    let ageDifference = 0;
    if (vitalSignPercentileUtility && props.vitalSign && props.currentPatient?.IndividualResponse?.DateOfBirth) {
        ageDifference = vitalSignPercentileUtility.getAgeFromPatientDateOfBirthAndPharmaStartDateDifference(
            props.currentPatient.IndividualResponse.DateOfBirth,
            props.vitalSign.PharmaStartDate
        );
    }

    const getPercentileStringWhenPercentileIsValid = (property: VitalProperty, vitalSign: MedicalItemVitalSign = null): string => {
        const percentile = vitalSignPercentileUtility?.calculatePercentileFromMedicalItemVitalSign(property, vitalSign);
        const percentileString = vitalSignPercentileUtility?.getPercentileText(property, { [property]: percentile })?.toString() ?? "";

        return percentile ? percentileString : "";
    };

    const renderPercentile = (percentileString: string): JSX.Element => <span className="text-italic">{percentileString}</span>;

    useEffect(() => {
        const loadGrowthCharts = async (): Promise<void> => {
            const growthChartDataModels = await GrowthChartApiClient.getGrowthChartsCacheData();
            setVitalSignPercentileUtility(new VitalSignPercentileUtility(growthChartDataModels, getVitalSignPatientData(props.currentPatient)));
        };

        loadGrowthCharts();
    }, [props.currentPatient]);

    const weightPercentileString = getPercentileStringWhenPercentileIsValid("Weight", props.vitalSign) ?? null;
    const heightPercentileString = getPercentileStringWhenPercentileIsValid("Height", props.vitalSign) ?? null;
    const headCircumferencePercentileString = getPercentileStringWhenPercentileIsValid("HeadCircumference", props.vitalSign) ?? null;

    const weightTitle = `${weight ?? null} ${weightPercentileString}`;
    const heightTitle = `${height ?? null} ${heightPercentileString}`;
    const bmiTitle = `${bmi ?? null} ${heightPercentileString}`;
    const headCircumferenceTitle = `${headCircumference ?? null} ${headCircumferencePercentileString}`;

    return (
        <div
            className={`${styles.vitalSignView} ${props.cssClassName ?? ""}`}
            onClick={() => props.onVitalSignClick?.()}
            data-cy="vital-signs-view"
        >

            <div title={bloodPressure}>
                <span className="text-gray">
                    {getText("BPSittingDLabel")}
                </span>
                <span data-cy="vital-sign-blood-pressure">
                    {bloodPressure}
                </span>
            </div>
            <div title={weightTitle}>
                <span className="text-gray">
                    {getText("WeightDLabel")}
                </span>
                <span data-cy="vital-sign-weight">
                    {weight} {!!weightPercentileString && renderPercentile(weightPercentileString)}
                </span>
            </div>
            <div title={heightTitle}>
                <span className="text-gray">
                    {getText("HeightDLabel")}
                </span>
                <span data-cy="vital-sign-height">
                    {height} {!!heightPercentileString && renderPercentile(heightPercentileString)}
                </span>
            </div>
            <div title={bmiTitle}>
                <span className="text-gray">
                    {getText("BMIDLabel")}
                </span>
                <span data-cy="vital-sign-bmi">
                    {bmi} {!!heightPercentileString && renderPercentile(heightPercentileString)}
                </span>
            </div>
            <div title={waistCircumference}>
                <span className="text-gray">
                    {getText("WaistCircumferenceDLabel")}
                </span>
                <span data-cy="vital-sign-waist-circumference">
                    {waistCircumference}
                </span>
            </div>
            <div title={(crcl === emptyText) ? emptyText : `${crcl} ml/min/1.73m^2`}>
                <span className="text-gray" data-cy="vital-sign-crcl-label">
                    {getText("CreaDLabel")}:
                </span>
                <span className={styles.vitalSignCrcl} data-cy="vital-sign-crcl">{crcl}
                    {(props.vitalSign && props.vitalSign.CreatinineClearance) && (
                        <span>
                            ml/min/1.73m<sup>2</sup>
                        </span>
                    )}
                </span>
                <span className={styles.vitalSignCrclAlgorithm} data-cy="vital-sign-crcl-algorithm">
                    {`( ${crclAlgorithm} )`}
                </span>
            </div>
            <div title={o2}>
                <span className="text-gray" data-cy="vital-sign-o2-label">
                    O<sub>2</sub> Sat:
                </span>
                <span data-cy="vital-sign-o2">
                    {o2}
                </span>
            </div>
            <div title={temperature}>
                <span className="text-gray">
                    T°:
                </span>
                <span data-cy="vital-sign-temperature">
                    {temperature}
                </span>
            </div>
            {ageDifference <= headCircumferenceAgeLimit && (
                <div title={headCircumferenceTitle}>
                    <span className="text-gray">
                        {getText("HCDLabel")}
                    </span>
                    <span data-cy="vital-sign-head-circumference">
                        {headCircumference} {!!headCircumferencePercentileString && renderPercentile(headCircumferencePercentileString)}
                    </span>
                </div>
            )}
            <div className={styles.vitalSignWrapper}>
                <div className={`${styles.vitalSignViewDate} ${props.cssClassNameDate ?? ""} ${props.hideVitalSignLabel ? styles.center : ""}`}>
                    {!props.hideVitalSignLabel && (
                        <span
                            onClick={e => { e.stopPropagation(); props.onArrowClick?.(); }}
                            data-cy="vital-sign-label"
                        >
                            {getText("VitalSignsLabel").toUpperCase()}
                        </span>
                    )}
                    <p data-cy="vital-sign-date">
                        {vitalSignDate}
                    </p>
                    <p data-cy="vital-sign-hour">
                        {vitalSignHour}
                    </p>
                </div>

                {props.showPlus && (
                    <div className={styles.vitalSignViewArrow}>
                        {props.showPlus && (
                            <PlusIconButton
                                onClick={e => { e.stopPropagation(); props.onPlusClick?.(); }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export { IVitalSignsViewProps, VitalSignsView };
